import first from "lodash/first";
import type { ReactNode } from "react";
import React, { createContext, useContext, useMemo, useState } from "react";
import type { NewsletterItem } from "scmp-sdk/components/newsletter/types";

import type { ValidNewsletterPackageKey } from "shared/data/newsletter";

import { Variant } from "./enums";

export type ContextValue = {
  campaignName?: string;
  dataMatrix?: Record<string, unknown>;
  findOutMoreButtonDisplayComponent?: ReactNode;
  lastSubscribedNewsletter?: string;
  newsletterItemForDisplay?: NewsletterItem;
  /**
   * This is passed from client side which contain required newsletters
   */
  newsletterItems: NewsletterItem[];
  packageDisplayName?: string;
  packageKey?: ValidNewsletterPackageKey;
  queryParametersForSocialButtons?: Record<string, string>;
  setLastSubscribedNewsletter?: (name: string) => void;
  /**
   * This is for hardcoding the button over the default value
   */
  signUpButtonDisplayComponent?: ReactNode;
  summaryComponent?: ReactNode;
  /**
   * This is for hardcoding the title over the newsletter title
   */
  titleComponent?: ReactNode;
  variant: Variant;
};

const NewsletterContext = createContext<ContextValue>({
  campaignName: "",
  dataMatrix: {},
  newsletterItems: [],
  queryParametersForSocialButtons: {},
  variant: Variant.Footer,
});

export const NewsletterContextProvider: React.FunctionComponent<{
  children?: ReactNode;
  multipleNewsletters?: boolean;
  value: ContextValue;
}> = ({ children, multipleNewsletters = false, value: { newsletterItems, ...context } }) => {
  const [lastSubscribedNewsletter, setLastSubscribedNewsletter] = useState("");
  const newsletterItemForDisplay = useMemo(() => first(newsletterItems), [newsletterItems]);
  return (
    <NewsletterContext.Provider
      value={{
        ...context,
        lastSubscribedNewsletter,
        newsletterItemForDisplay,
        newsletterItems: multipleNewsletters ? newsletterItems : newsletterItems.slice(0, 1),
        setLastSubscribedNewsletter,
      }}
    >
      {children}
    </NewsletterContext.Provider>
  );
};

NewsletterContext.displayName = "NewsletterContext";

export const useNewsletterContext = () => useContext(NewsletterContext);
